import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Alert,
    Box,
    CircularProgress,
    FormControlLabel,
    MenuItem,
    Snackbar,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import axios from "axios";
import NavBarBottom from "../../components/NavBarBottom";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import ManualPicksPanel from "../../components/ManualPicksPanel";
import CaptureImage from "../../components/CaptureImage";
import useManualPicks from "../../helper/useManualPicks";
import {
    PlateGrid,
    PatchGrid,
    slotLabelLines,
    passKindDescription,
} from "../colorExploration/ExplorationPlates";
import {
    MANUAL_LOGO_SELECTION,
    isManualSelection,
    isSelectionFinalized,
    selectionResumeRoute,
    selectionSidebar,
} from "../../helper/selectionFlow";
import { useSelectionParams } from "../../helper/entityRoutes";

/** Largest patch cell on this page (px): big enough to hit, small enough
 *  that a 10 x 10 patch group still fits beside the palette panel. */
const MAX_PATCH_CELL = 76;
/** Slot size of the plate map (px): a compact overview of the physical
 *  plate, so the opened patch group stays in view below it. */
const PLATE_MAP_CELL = 72;

const errorText = (err, fallback) =>
    err?.response?.data?.error || err?.response?.data?.message || fallback;

/** A plate slot is one patch group in one marking pass. */
const slotKey = (slot) => (slot ? `${slot.patch_group_id}:${slot.pass_kind}` : "");

/** The tile a plate opens on: its first colour-pass slot (the colours to
 *  compare with the marked plate), else its first slot. */
function defaultSlot(slots) {
    return slots.find((slot) => slot.pass_kind === "COLOR") || slots[0] || null;
}

function Loading({ text }) {
    return (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1.5 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">{text}</Typography>
        </Stack>
    );
}

/**
 * Manual logo selection, "Pick on Plates": the palette is picked off the
 * marked plates.
 *
 * The plates are drawn as the exploration marked them (the exploration's
 * Plates view): choose the exploration and the plate, click a tile, and its
 * patches open in their marked positions, tinted with the measured colour.
 * Clicking a patch adds its sample to the palette, clicking it again removes
 * it. Patches that cannot be picked (no colour or no line width measured)
 * are dimmed.
 */
export default function ColorSelectionManualPlates() {
    const navigate = useNavigate();
    const { selectionId, stepRoute } = useSelectionParams();

    const [ready, setReady] = useState(false);
    const [pageError, setPageError] = useState(null);
    const [explorations, setExplorations] = useState([]);
    const [candidateIds, setCandidateIds] = useState(() => new Set());
    const [explorationId, setExplorationId] = useState("");
    const [plates, setPlates] = useState([]);
    const [platesLoading, setPlatesLoading] = useState(false);
    const [plateId, setPlateId] = useState("");
    const [slots, setSlots] = useState([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [slot, setSlot] = useState(null);
    const [group, setGroup] = useState(null);
    const [groupLoading, setGroupLoading] = useState(false);
    const [groupError, setGroupError] = useState(null);
    const [showPhoto, setShowPhoto] = useState(false);

    const palette = useManualPicks(ready ? selectionId : null);

    // The selection must be an unfinished manual one; anything else goes to
    // where it can be continued. Then the pool and the explorations load.
    useEffect(() => {
        let cancelled = false;
        const init = async () => {
            try {
                const selectionResponse = await axios.get(`api/color/selection/${selectionId}/`);
                const { status, selection_type: selectionType } = selectionResponse.data || {};
                if (!isManualSelection(selectionType) || isSelectionFinalized(status)) {
                    navigate(selectionResumeRoute(selectionId, status, selectionType), { replace: true });
                    return;
                }
                const candidates = await axios.post("api/selection/manual/candidates", { selectionId });
                if (cancelled) return;
                setCandidateIds(new Set((candidates.data.sample_ids || []).map(String)));
                const rows = candidates.data.explorations || [];
                setExplorations(rows);
                setExplorationId(rows[0]?.id || "");
                setReady(true);
            } catch (err) {
                if (!cancelled) setPageError(errorText(err, "Loading the selection failed."));
            }
        };
        init();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!explorationId) return undefined;
        let cancelled = false;
        setPlatesLoading(true);
        setPlates([]);
        setPlateId("");
        axios
            .get(`api/exploration/${explorationId}/plates/`)
            .then((response) => {
                if (cancelled) return;
                const rows = response.data?.plates || [];
                setPlates(rows);
                setPlateId(rows[0]?.id || "");
            })
            .catch((err) => {
                if (!cancelled) setPageError(errorText(err, "Loading the plates failed."));
            })
            .finally(() => {
                if (!cancelled) setPlatesLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [explorationId]);

    useEffect(() => {
        setSlots([]);
        setSlot(null);
        if (!explorationId || !plateId) return undefined;
        let cancelled = false;
        setSlotsLoading(true);
        axios
            .get(`api/exploration/${explorationId}/plates/${plateId}/occupancy/`)
            .then((response) => {
                if (cancelled) return;
                const rows = response.data?.slots || [];
                setSlots(rows);
                setSlot(defaultSlot(rows));
            })
            .catch((err) => {
                if (!cancelled) setPageError(errorText(err, "Loading the plate failed."));
            })
            .finally(() => {
                if (!cancelled) setSlotsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [explorationId, plateId]);

    useEffect(() => {
        setGroup(null);
        setGroupError(null);
        if (!explorationId || !plateId || !slot) return undefined;
        let cancelled = false;
        setGroupLoading(true);
        axios
            .get(
                `api/exploration/${explorationId}/plates/${plateId}/patch-group/${slot.patch_group_id}/patches/`,
                { params: { pass_kind: slot.pass_kind || "CLUSTER" } }
            )
            .then((response) => {
                if (!cancelled) setGroup(response.data);
            })
            .catch((err) => {
                if (!cancelled) setGroupError(errorText(err, "Loading the patches failed."));
            })
            .finally(() => {
                if (!cancelled) setGroupLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [explorationId, plateId, slot]);

    const colourMeasured = (patch) =>
        patch?.performance?.l_star !== null && patch?.performance?.l_star !== undefined;
    const isPickable = (patch) => candidateIds.has(String(patch.sample_id)) && colourMeasured(patch);
    const tooltipLines = (patch) => {
        let value = "Click to add";
        if (palette.isPicked(patch.sample_id)) value = "Picked · click to remove";
        else if (!colourMeasured(patch)) value = "No colour measured";
        else if (!candidateIds.has(String(patch.sample_id))) value = "No line width measured";
        return [{ label: "palette", value }];
    };

    const selectedPlate = plates.find((plate) => plate.id === plateId) || null;
    const labelLines = slotLabelLines(slot);
    const groupPatches = group?.patches || [];
    const groupPickable = groupPatches.filter(isPickable).length;
    const groupPicked = groupPatches.filter((patch) => palette.isPicked(patch.sample_id)).length;

    return (
        <Stack alignItems="center" sx={{ mt: "2%", mb: 4, width: "100%" }} spacing={2}>
            <ExplorationPageShell
                title="Pick on Plates"
                subtitle="Click a patch to add its sample to the palette; click it again to remove it."
                stepSidebar={selectionSidebar("plates", palette.picks.length, MANUAL_LOGO_SELECTION)}
                footerBelowPaper={
                    <Box sx={{ width: "100%" }}>
                        <NavBarBottom
                            variant="embedded"
                            returnLabel="Back"
                            prevPage={stepRoute("explorations", MANUAL_LOGO_SELECTION)}
                            nextPage={stepRoute("search", MANUAL_LOGO_SELECTION)}
                            enableNextPage={!ready}
                            nextPageClickFunc={(page) => navigate(page)}
                        />
                    </Box>
                }
            >
                {pageError ? <Alert severity="error" sx={{ mb: 2 }}>{pageError}</Alert> : null}
                {!ready && !pageError ? <Loading text="Loading the selection…" /> : null}

                {ready ? (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", lg: "row" },
                            gap: 3,
                            alignItems: "flex-start",
                        }}
                    >
                        <Stack spacing={2.5} sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                <TextField
                                    select
                                    size="small"
                                    label="Exploration"
                                    value={explorationId}
                                    onChange={(event) => setExplorationId(event.target.value)}
                                    sx={{ flex: 1, minWidth: 240 }}
                                >
                                    {explorations.map((row) => (
                                        <MenuItem key={row.id} value={row.id}>{row.name}</MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    select
                                    size="small"
                                    label="Plate"
                                    value={plateId}
                                    onChange={(event) => setPlateId(event.target.value)}
                                    disabled={platesLoading || plates.length === 0}
                                    sx={{ minWidth: 200 }}
                                >
                                    {plates.map((plate) => (
                                        <MenuItem key={plate.id} value={plate.id}>
                                            {plate.name || `Plate ${plate.plate_number}`}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Stack>

                            {platesLoading ? <Loading text="Loading the plates…" /> : null}
                            {!platesLoading && explorationId && plates.length === 0 ? (
                                <Alert severity="info">This exploration has no marked plates.</Alert>
                            ) : null}

                            {selectedPlate ? (
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                        {selectedPlate.name || `Plate ${selectedPlate.plate_number}`}
                                    </Typography>
                                    {slotsLoading ? (
                                        <Loading text="Loading the plate…" />
                                    ) : (
                                        <PlateGrid
                                            plate={selectedPlate}
                                            slots={slots}
                                            onSelectSlot={setSlot}
                                            isSlotSelected={(cell) => slotKey(cell) === slotKey(slot)}
                                            maxWidth={Math.min(520, Math.max(selectedPlate.cnt_x || 1, 1) * PLATE_MAP_CELL)}
                                        />
                                    )}
                                </Box>
                            ) : null}

                            {slot ? (
                                <Box
                                    data-testid="patch-group"
                                    sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, p: 2 }}
                                >
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="baseline"
                                        flexWrap="wrap"
                                        useFlexGap
                                        spacing={1}
                                        sx={{ mb: 1.5 }}
                                    >
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                {labelLines
                                                    ? `${labelLines.iterationLine} · ${labelLines.batchLine}`
                                                    : "Patch group"}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {passKindDescription(slot.pass_kind) || ""}
                                            </Typography>
                                        </Box>
                                        {group ? (
                                            <Typography variant="body2" color="text.secondary" data-testid="patch-group-counts">
                                                {`${groupPicked} picked · ${groupPickable} of ${groupPatches.length} pickable`}
                                            </Typography>
                                        ) : null}
                                    </Stack>
                                    {groupError ? <Alert severity="error">{groupError}</Alert> : null}
                                    {groupLoading ? <Loading text="Loading the patches…" /> : null}
                                    {group ? (
                                        <>
                                            <Box
                                                sx={{
                                                    width: "100%",
                                                    maxWidth: Math.max(group.columns || 1, 1) * MAX_PATCH_CELL,
                                                }}
                                            >
                                                <PatchGrid
                                                    data={group}
                                                    passKind={slot.pass_kind}
                                                    onSelectPatch={(patch) => palette.toggle(patch.sample_id)}
                                                    isPatchSelectable={isPickable}
                                                    isPatchPicked={(patch) => palette.isPicked(patch.sample_id)}
                                                    patchTooltipLines={tooltipLines}
                                                    gap={0.75}
                                                />
                                            </Box>
                                            <FormControlLabel
                                                sx={{ mt: 1.5 }}
                                                control={
                                                    <Switch
                                                        size="small"
                                                        checked={showPhoto}
                                                        onChange={(event) => setShowPhoto(event.target.checked)}
                                                    />
                                                }
                                                label={<Typography variant="body2">Plate photo</Typography>}
                                            />
                                            {showPhoto ? (
                                                <Box sx={{ mt: 1, maxWidth: 640 }}>
                                                    <CaptureImage
                                                        explorationId={explorationId}
                                                        plateId={plateId}
                                                        patchGroupId={slot.patch_group_id}
                                                        passKind={slot.pass_kind}
                                                        sourceImage={group.source_image}
                                                    />
                                                </Box>
                                            ) : null}
                                        </>
                                    ) : null}
                                </Box>
                            ) : null}
                        </Stack>

                        <Box sx={{ width: { xs: "100%", lg: 300 }, flexShrink: 0 }}>
                            <ManualPicksPanel
                                selectionId={selectionId}
                                picks={palette.picks}
                                loading={palette.loading}
                                onRemove={(sampleId) => palette.remove([sampleId])}
                                onClear={palette.clear}
                            />
                        </Box>
                    </Box>
                ) : null}
            </ExplorationPageShell>

            <Snackbar
                open={Boolean(palette.error)}
                autoHideDuration={6000}
                onClose={palette.clearError}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="error" onClose={palette.clearError} sx={{ width: "100%" }}>
                    {palette.error}
                </Alert>
            </Snackbar>
        </Stack>
    );
}
